import type { AppError } from '../../errors/types';
import { normalizeApiError } from '../../errors/normalize-api-error';
import type {
  ChangePasswordRequest,
  ChangePasswordResponse,
  LoginRequest,
  LoginResponse,
  RefreshRequest,
  RefreshResponse,
} from './auth.types';

export class AuthApiError extends Error {
  readonly appError: AppError;

  constructor(appError: AppError) {
    super(appError.message ?? appError.code);
    this.name = 'AuthApiError';
    this.appError = appError;
  }
}

const BASE = '/auth';

async function parseError(response: Response): Promise<AuthApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // body no es JSON
  }
  const source =
    body && typeof body === 'object'
      ? {
          ...(body as Record<string, unknown>),
          statusCode:
            (body as Record<string, unknown>).statusCode ?? response.status,
        }
      : { statusCode: response.status };
  return new AuthApiError(normalizeApiError(source, { headers: response.headers }));
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new AuthApiError(normalizeApiError(err));
  }
  if (!response.ok) {
    throw await parseError(response);
  }
  return (await response.json()) as T;
}

export function login(request: LoginRequest): Promise<LoginResponse> {
  return postJson<LoginResponse>(`${BASE}/login`, request);
}

export function changePassword(
  request: ChangePasswordRequest,
): Promise<ChangePasswordResponse> {
  return postJson<ChangePasswordResponse>(`${BASE}/change-password`, request);
}

export function refresh(request: RefreshRequest): Promise<RefreshResponse> {
  return postJson<RefreshResponse>(`${BASE}/refresh`, request);
}

export const authApi = { login, changePassword, refresh };
